<script setup>
import { ref, onMounted, onUnmounted, computed, watch, defineAsyncComponent } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import {bbox} from '@turf/turf'

import { toast } from '@/components/ui/toast'
const FeaturesList = defineAsyncComponent(() => import('@/components/features/FeaturesList.vue'))
import { Spinner } from '@/components/ui/spinner'
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev,
} from '@/components/ui/pagination'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Filter, ChevronDown, ChevronUp } from 'lucide-vue-next'
import ProjectFilters from '@/pages/Layers/Detail/ProjectFilters.vue'
import ProjectTabs from '@/pages/Project/Components/ProjectTabs.vue'
import { useFeaturesFeed } from '@/composables/useFeaturesFeed'

const store = useStore()
const member = computed(() => store.getters['user/getMember'])
const totalInFocus = computed(() => store.getters['geo/getTotalOnMap'])
const isLoggedIn = computed(() => store.getters.getIsLoggedIn)
const map = computed(() => store.getters['geo/getMap'])
const features = computed(() => store.getters['geo/getFeedFeatures'])

const dataFilter = ref(localStorage.getItem('dataFilter') || 'all') // 'all', 'private'
const isFetchingBoundary = ref(false)

const router = useRouter()
const route = useRoute()
const projectId = computed(() => parseInt(route.params.project_id))
const orgId = computed(() => route.params.org_id)

const additionalParams = computed(() => {
  if (dataFilter.value === 'private') {
    return {
      shared: false,
      member_id: member.value?.member_id
    }
  }
  return null
})

const {
  total,
  page,
  limit,
  offset,
  isLoading,
  viewportOnly,
  showFilters,
  currentFilters,
  hasActiveFilters,
  displayFeatures,
  fetchFeatures,
  fetchDates,
  toggleViewportOnly: composableToggleViewportOnly,
  updateFilters,
  paginationUpdated,
} = useFeaturesFeed({ orgId, projectId, layerCode: null, additionalParams })

const displayFeaturesLength = computed(() => displayFeatures.value?.length)
const featuresLength = computed(() => features.value.features?.length)
const isFeatureCreated = computed(() => store.getters['geo/getIsFeatureCreated'])

onMounted(async () => {
  if (isLoggedIn.value) {
    await fetchDates()
    await fetchFeatures()
  }
})

onUnmounted(() => {
  store.commit('layers/CLEAR_DATES')
  store.commit('geo/SET_PROJECT_FILTERS', null)
})

watch(
  () => isLoggedIn.value,
  async (newValue, oldValue) => {
    if (newValue && !oldValue) {
      await fetchDates()
      await fetchFeatures()
    }
  }
)

watch(
  () => projectId.value,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      store.commit('layers/CLEAR_DATES')
      await fetchDates()
      await fetchFeatures()
    }
  }
)

watch(
  () => isFeatureCreated.value,
  async (value) => {
    if (value) {
      await fetchFeatures()
      store.commit('geo/SET_FEATURE_CREATED', false)
    }
  }
)

watch(
  () => dataFilter.value,
  async (newValue, oldValue) => {
    if (newValue !== oldValue) {
      page.value = 1
      await fetchFeatures()
    }
  }
)

const focusOnFilteredData = async () => {
  isFetchingBoundary.value = true
  try {
    const boundary = await store.dispatch('boundary/fetchProjectBoundary', projectId.value)

    if (!boundary) {
      isFetchingBoundary.value = false
      return
    }

    const geometry = boundary.geometry

    if (!geometry || !Array.isArray(geometry.coordinates) || geometry.coordinates.length === 0) {
      isFetchingBoundary.value = false
      return
    }

    const bounds = bbox(geometry)
    if (!bounds || bounds.length !== 4) {
      isFetchingBoundary.value = false
      return
    }
    map.value.fitBounds(bounds)
  } catch (error) {
    console.error('Failed to fetch project boundary', error)
  } finally {
    isFetchingBoundary.value = false
  }
}

const setDataFilter = async (filter) => {
  dataFilter.value = filter
  localStorage.setItem('dataFilter', filter)
}

const toggleViewportOnly = async (value) => {
  await composableToggleViewportOnly(value)
  if (!value) {
    await focusOnFilteredData()
  }
}
</script>

<template>
  <ProjectTabs />

  <div class="px-3 py-2">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2 text-muted-foreground">
        <span>Total: {{ total }}</span>
        <span v-if="viewportOnly">&middot; In View: {{ totalInFocus }}</span>
      </div>
      <Button
        @click="showFilters = !showFilters"
        variant="ghost"
        size="sm"
        class="text-primary hover:bg-primary/10 relative"
      >
        <Filter class="h-4 w-4 mr-1" />
        Filter
        <span v-if="hasActiveFilters || dataFilter === 'private'" class="ml-1 w-2 h-2 bg-primary rounded-full inline-block" />
        <component :is="showFilters ? ChevronUp : ChevronDown" class="h-4 w-4 ml-1" />
      </Button>
    </div>

    <div v-if="showFilters" class="border rounded-lg p-3 mt-2 bg-muted/50 space-y-3">
      <div class="flex items-center">
        <Switch :model-value="viewportOnly" @update:model-value="toggleViewportOnly" />
        <span class="text-sm font-medium ml-2">Show data in map area</span>
      </div>

      <div class="flex items-center">
        <Switch
          :model-value="dataFilter === 'private'"
          @update:model-value="setDataFilter($event ? 'private' : 'all')"
        />
        <span class="text-sm font-medium ml-2">Show only my data</span>
      </div>

      <ProjectFilters
        :org-id="orgId"
        :project-id="projectId"
        :hide-project-selector="true"
        @update:filters="updateFilters"
      />
    </div>
  </div>

  <div v-if="displayFeaturesLength > 0">
    <!-- Features List with Selection -->
    <div class="relative">
      <div :class="{ 'opacity-50 transition-opacity duration-200': isLoading }">
        <FeaturesList :features="displayFeatures" />
      </div>
      <div v-if="isLoading" class="absolute inset-x-0 top-2 flex justify-center pointer-events-none">
        <span class="text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded shadow-sm">Updating...</span>
      </div>
    </div>

    <div v-if="(viewportOnly ? totalInFocus : total) > limit && !isLoading" class="p-3">
      <Pagination
        v-slot="{ page }"
        :total="viewportOnly ? totalInFocus : total"
        :sibling-count="1"
        show-edges
        :default-page="page"
        :items-per-page="limit"
        @update:page="paginationUpdated"
      >
        <PaginationList v-slot="{ items }" class="flex items-center gap-1">
          <PaginationFirst />
          <PaginationPrev />

          <template v-for="(item, index) in items">
            <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === page ? 'default' : 'outline'">
                {{ item.value }}
              </Button>
            </PaginationListItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>

          <PaginationNext />
          <PaginationLast />
        </PaginationList>
      </Pagination>
    </div>
  </div>
  <div v-else class="p-3 w-full h-full flex items-center justify-center">
    <div v-if="!isLoading && featuresLength === 0" class="mt-0 text-center">
      <div v-if="viewportOnly">
        <h3 class="text-base font-bold">No data in view</h3>
        <p class="text-muted-foreground">Try panning or zooming out to find data.</p>
        <Button
          @click="toggleViewportOnly(false)"
          class="text-primary mt-3"
          variant="secondary"
          size="sm"
        >
          Switch to all data
        </Button>
      </div>
      <div v-else>
        <h3 class="text-base font-bold">No data found</h3>
        <p class="text-muted-foreground">
          Adjust filters or select a different project.
        </p>
      </div>
    </div>
  </div>
</template>
